import type { Observation } from './continuity.ts';
import { isContiguousSlice } from './continuity.ts';
import { isHit } from './target.ts';

export const DEFAULT_STATE_CUTS = [1.2, 2, 5, 10, 50, 100] as const;
export const DEFAULT_STATE_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
export const DEFAULT_PATTERN_TARGETS = [2, 5, 10, 50, 100] as const;
export const PROCESS_MEMORY_ALGORITHM_VERSION = 'process-memory-v1';
export const DEFAULT_PERMUTATION_SEED = 20_260_918;
export const DEFAULT_PERMUTATION_COUNT = 1000;
export const DEFAULT_MIN_PATTERN_SAMPLE = 30;
export const DEFAULT_MIN_EFFECT_SIZE = 0.05;
export const DEFAULT_TRIMMED_MEAN_FRACTION = 0.1;
export const DISCOVERY_FRACTION = 0.6;
export const VALIDATION_FRACTION = 0.2;
export const HOLDOUT_FRACTION = 0.2;

export type SplitRole = 'discovery' | 'validation' | 'holdout';

export interface MemoryRound extends Observation {
  stateIndex: number;
  stateLabel: string;
}

export function assertIncreasingCuts(cuts: readonly number[]): void {
  if (cuts.length === 0) {
    throw new Error('State cuts must contain at least one threshold.');
  }
  for (let i = 1; i < cuts.length; i += 1) {
    const prev = cuts[i - 1];
    const next = cuts[i];
    if (prev === undefined || next === undefined || !(next > prev)) {
      throw new Error('State cuts must be strictly increasing.');
    }
  }
}

export function stateLabelsForCuts(cuts: readonly number[]): string[] {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const count = cuts.length + 1;
  if (count > alphabet.length) {
    throw new Error('Too many state cuts.');
  }
  return [...alphabet.slice(0, count)];
}

/**
 * Classify a multiplier into ordered states using left-closed, right-open bins
 * except the last bin which is closed: [cut_i, cut_{i+1}) with the first bin (-∞, cut_0)
 * implemented as multiplier < cut_0, and the last bin multiplier >= cut_last.
 */
export function classifyMultiplierState(
  multiplier: number,
  cuts: readonly number[] = DEFAULT_STATE_CUTS,
  labels: readonly string[] = DEFAULT_STATE_LABELS,
): { index: number; label: string } {
  if (!Number.isFinite(multiplier)) {
    throw new Error('Multiplier must be finite.');
  }
  assertIncreasingCuts(cuts);
  const resolvedLabels = labels.length === cuts.length + 1 ? labels : stateLabelsForCuts(cuts);
  for (let i = 0; i < cuts.length; i += 1) {
    const cut = cuts[i];
    if (cut !== undefined && multiplier < cut) {
      return { index: i, label: resolvedLabels[i] ?? String(i) };
    }
  }
  const last = cuts.length;
  return { index: last, label: resolvedLabels[last] ?? String(last) };
}

export function attachStates(
  rounds: readonly Observation[],
  cuts: readonly number[] = DEFAULT_STATE_CUTS,
): { rounds: MemoryRound[]; labels: string[] } {
  const labels = stateLabelsForCuts(cuts);
  return {
    labels,
    rounds: rounds.map((round) => {
      const classified = classifyMultiplierState(round.multiplier, cuts, labels);
      return { ...round, stateIndex: classified.index, stateLabel: classified.label };
    }),
  };
}

export function replaceMultipliers(rounds: readonly MemoryRound[], multipliers: readonly number[], cuts: readonly number[]): MemoryRound[] {
  const labels = stateLabelsForCuts(cuts);
  return rounds.map((round, index) => {
    const multiplier = multipliers[index];
    if (multiplier === undefined) {
      throw new Error('Multiplier permutation length mismatch.');
    }
    const classified = classifyMultiplierState(multiplier, cuts, labels);
    return { ...round, multiplier, stateIndex: classified.index, stateLabel: classified.label };
  });
}

export function chronologicalSplitRoles(n: number): SplitRole[] {
  const discoveryEnd = Math.floor(n * DISCOVERY_FRACTION);
  const validationEnd = Math.floor(n * (DISCOVERY_FRACTION + VALIDATION_FRACTION));
  const roles: SplitRole[] = [];
  for (let i = 0; i < n; i += 1) {
    if (i < discoveryEnd) {
      roles.push('discovery');
    } else if (i < validationEnd) {
      roles.push('validation');
    } else {
      roles.push('holdout');
    }
  }
  return roles;
}

export function sameSplitAndSegment(
  rounds: readonly Observation[],
  startIndex: number,
  length: number,
  roles: readonly SplitRole[],
): boolean {
  if (!isContiguousSlice(rounds, startIndex, length)) {
    return false;
  }
  const role = roles[startIndex];
  if (role === undefined) {
    return false;
  }
  for (let i = 1; i < length; i += 1) {
    if (roles[startIndex + i] !== role) {
      return false;
    }
  }
  return true;
}

/** Lag-k pairs that stay inside one continuity segment. Lag is in included-round steps. */
export function lagPairIndices(rounds: readonly Observation[], lag: number): Array<[number, number]> {
  if (!(lag >= 1)) {
    throw new Error('Lag must be a positive integer.');
  }
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i + lag < rounds.length; i += 1) {
    const left = rounds[i];
    const right = rounds[i + lag];
    if (left && right && left.segmentId === right.segmentId) {
      pairs.push([i, i + lag]);
    }
  }
  return pairs;
}

export function segmentRuns(rounds: readonly Observation[]): Array<{ segmentId: number; start: number; endExclusive: number }> {
  const runs: Array<{ segmentId: number; start: number; endExclusive: number }> = [];
  for (let i = 0; i < rounds.length; ) {
    const current = rounds[i];
    if (!current) {
      i += 1;
      continue;
    }
    let end = i + 1;
    while (end < rounds.length && rounds[end]?.segmentId === current.segmentId) {
      end += 1;
    }
    runs.push({ segmentId: current.segmentId, start: i, endExclusive: end });
    i = end;
  }
  return runs;
}

export function hitsAt(rounds: readonly { multiplier: number }[], target: number): Int8Array {
  const hits = new Int8Array(rounds.length);
  for (let i = 0; i < rounds.length; i += 1) {
    const round = rounds[i];
    hits[i] = round && isHit(round.multiplier, target) ? 1 : 0;
  }
  return hits;
}

export function inclusionLabels(inclusion: { confirmed: boolean; recovered: boolean; uncertain: boolean }): string[] {
  const labels: string[] = [];
  if (inclusion.confirmed) {
    labels.push('CONFIRMED');
  }
  if (inclusion.recovered) {
    labels.push('RECOVERED');
  }
  if (inclusion.uncertain) {
    labels.push('UNCERTAIN');
  }
  return labels;
}
